import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router";
import { api } from "../../data";
import { useStaffWorkspace, type StaffWorkspace } from "../../app/session";
import { ErrorState, Loading } from "../../components/States";
import { PageHead, useRueckfrage } from "../../components/ui";
import { toast } from "../../components/Toast";
import { fehlerText } from "../../lib/fehler";
import { monthLabel } from "../../lib/format";
import { TestLaeuftZeile } from "./Startleitfaden";
import { currentLang, t } from "../../lib/sprache";
import { TextField } from "./einstellungen/parts";
import {
  centAus, gemerkt, legeBeispieldatenAn, legeCreatorAn, legeKooperationAn, loadSchnellstart, loescheBeispieldaten, merke, pruefeCreator, pruefeKoop,
  vertragsentwurfAusMuster, type CreatorEingabe, type KoopEingabe, type SchnellstartStand,
} from "./schnellstart-daten";
import { useRundgang } from "./coco-rundgang";
import { useNurLesen } from "../../app/nurLesen";

/**
 * Schnellstart, 3 Minuten (Aktivierungsplan Vertrieb 16.10.2026, Punkt 1). Ein neuer Agentur-Admin landet nach Registrierung,
 * Bestätigung oder übersprungener Zahlungsart hier statt auf der leeren Übersicht (Weiche in Uebersicht.tsx):
 *   1  ersten Creator anlegen (Name, Instagram-Handle freiwillig; K-558 c wie Coco und „Creator anlegen“),
 *   2  Kooperation anlegen (Marke per Name im selben Formular, Betrag je Monat, Zeitraum),
 *   3  ansehen, was daraus wird (Vertragsentwurf aus dem Muster, Rechnungsentwurf, Content-Freigabe); nichts wird versendet.
 * Gleichwertig daneben „Mit Beispieldaten ansehen“ (0325). Deep-Links aus den Aktivierungs-Mails: ?schritt=creator|kooperation,
 * ?beispiel=1 (hebt den Knopf hervor). Baut auf dem Startleitfaden auf (gleiche Liste .tk-first-steps / .tk-steps).
 */
export const schnellstartKey = (orgId: string) => ["schnellstart", orgId] as const;
const heuteIso = () => new Date().toISOString().slice(0, 10);
const monatJetzt = () => new Date().toISOString().slice(0, 7);
const sprache = (): "de" | "en" => (currentLang() === "en" ? "en" : "de");

export function Schnellstart() {
  const ws = useStaffWorkspace();
  // Befund 5b (21.10.2026): im Nur-Lesen-Zustand gibt es nichts anzulegen
  const nurLesen = useNurLesen();
  if (!ws.isChef || ws.isCreator || nurLesen) return <Navigate to="/agentur" replace />;
  return <SchnellstartSeite ws={ws} />;
}

export function SchnellstartSeite({ ws }: { ws: StaffWorkspace }) {
  const orgId = ws.org.org_id;
  const stand = useQuery({ queryKey: schnellstartKey(orgId), queryFn: () => loadSchnellstart(api, orgId) });
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { ask, element: rueckfrage } = useRueckfrage();
  const [angesehen, setAngesehen] = useState(() => gemerkt(orgId, "angesehen"));
  const [beispielBusy, setBeispielBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const beispielKnopf = useRef<HTMLButtonElement>(null);
  const schritt = params.get("schritt");
  const beispielHervor = params.get("beispiel") === "1";

  useEffect(() => {
    if (!stand.data) return;
    const ziel = schritt === "kooperation" ? "schnellstart-schritt-2" : schritt === "creator" ? "schnellstart-schritt-1" : null;
    if (ziel) document.getElementById(ziel)?.scrollIntoView({ block: "center" });
    if (beispielHervor) beispielKnopf.current?.focus();
  }, [!!stand.data, schritt, beispielHervor]);

  if (stand.isLoading) return <main id="main" className="content"><Loading /></main>;
  if (stand.error || !stand.data) return <main id="main" className="content"><ErrorState error={stand.error} /></main>;
  const s = stand.data;
  const neu = () => qc.invalidateQueries({ queryKey: schnellstartKey(orgId) }).then(() => qc.invalidateQueries({ queryKey: ["agentur-start", orgId] }));
  const erledigt1 = s.creators > 0;
  const erledigt2 = s.deals > 0;
  const erledigt3 = erledigt2 && (angesehen || !!s.dealContract || !!s.dealInvoice);
  const zahl = [erledigt1, erledigt2, erledigt3].filter(Boolean).length;
  const ansehen = () => { merke(orgId, "angesehen"); setAngesehen(true); };

  const beispieldaten = async () => {
    setBeispielBusy(true);
    setFehler(null);
    try {
      await legeBeispieldatenAn(api, orgId, { orgName: ws.org.name, language: sprache(), today: heuteIso() });
      await neu();
      toast(t("Beispieldaten sind da. Sie senden nichts und zählen nicht in deinen Tarif.", "Sample data is ready. It sends nothing and does not count towards your plan."));
    } catch (e) {
      setFehler(fehlerText(e));
    } finally {
      setBeispielBusy(false);
    }
  };
  const loeschen = async () => {
    const ja = await ask({ title: t("Beispieldaten löschen?", "Delete sample data?"), text: t("Creator „Mila Beispiel“, die Beispielmarke, die Beispiel-Kooperation mit Vertrag, Freigabe und Rechnungsentwurf werden entfernt. Deine eigenen Daten bleiben.", "The creator “Mila Beispiel”, the sample brand and the sample collaboration with its contract, approval and invoice draft are removed. Your own data stays."), confirmLabel: t("Beispieldaten löschen", "Delete sample data"), danger: true });
    if (!ja) return;
    try {
      await loescheBeispieldaten(api, orgId);
      await qc.invalidateQueries();
      toast(t("Beispieldaten gelöscht.", "Sample data deleted."));
    } catch (e) {
      setFehler(fehlerText(e));
    }
  };

  return (
    <main id="main" className="content tk-schnellstart">
      <PageHead eyebrow={ws.org.name} title={t("Schnellstart, 3 Minuten", "Quick start, 3 minutes")}
        text={t("Drei Schritte, dann siehst du, wie CreatorOffice für deine Agentur arbeitet. Verschickt wird dabei nichts.", "Three steps, and you'll see how CreatorOffice works for your agency. Nothing is sent along the way.")} />
      {/* Inhaber 21.10.2026: nach dem Abschluss mit Zahlungsart die Zeile zum Test, nicht „Abo aktiv“ */}
      <TestLaeuftZeile ws={ws} />

      {!s.has_sample && !erledigt1 && !erledigt2 && (
        <section className="panel stack tk-schnellstart-wahl" aria-label={t("Wie möchtest du starten?", "How would you like to start?")}>
          <div className="tk-schnellstart-knoepfe">
            {/* D-2: die Wahl als ruhige Knöpfe; gefüllt ist der Knopf im ersten Schritt (bzw. der hervorgehobene Beispielknopf) */}
            <a className="btn" href="#schnellstart-schritt-1" onClick={(e) => { e.preventDefault(); document.querySelector<HTMLInputElement>("#schnellstart-schritt-1 input")?.focus(); }}>
              {t("Mit eigenen Daten starten", "Start with your own data")}
            </a>
            <button ref={beispielKnopf} type="button" className={`btn${beispielHervor ? " primary tk-hervor" : ""}`} disabled={beispielBusy} aria-busy={beispielBusy} onClick={() => void beispieldaten()}>
              {beispielBusy ? t("Beispieldaten werden angelegt …", "Creating sample data …") : t("Mit Beispieldaten ansehen", "Explore with sample data")}
            </button>
          </div>
          <small>{t("Beispieldaten sind erfunden (Creator „Mila Beispiel“, Beispielmarke GmbH), klar als Beispiel markiert, senden nichts und lassen sich mit einem Klick wieder löschen.", "Sample data is made up (creator “Mila Beispiel”, Beispielmarke GmbH), clearly marked as a sample, sends nothing and can be deleted again with one click.")}</small>
          {fehler && <p className="tk-error" role="alert">{fehler}</p>}
        </section>
      )}

      {s.has_sample && <BeispielKarte s={s} orgId={orgId} onLoeschen={() => void loeschen()} onNeu={neu} orgName={ws.org.name} />}
      {s.has_sample && fehler && <p className="tk-error" role="alert">{fehler}</p>}

      <section className="panel stack tk-first-steps" aria-labelledby="schnellstart-schritte">
        <h2 id="schnellstart-schritte">{t("Deine ersten Schritte", "Your first steps")} <span className="tk-steps-stand">{t(`${zahl} von 3 erledigt`, `${zahl} of 3 done`)}</span></h2>
        <div className="progress" role="img" aria-label={t(`${zahl} von 3 Schritten erledigt`, `${zahl} of 3 steps done`)}><span style={{ width: `${Math.round((zahl / 3) * 100)}%` }}></span></div>
        <ol className="tk-steps tk-schnellstart-schritte">
          <Schritt nr={1} id="schnellstart-schritt-1" done={erledigt1} aktiv={!erledigt1}
            titel={t("Ersten Creator anlegen", "Add your first creator")}
            hinweis={t("Für den Anfang reicht der Name, den Instagram-Handle und alles andere ergänzt du gern gleich oder später.", "A name is enough to start; add the Instagram handle and everything else now or later.")}
            fertig={s.creator && <>{s.creator.name} · <Link to={`/agentur/creator/${s.creator.id}`}>{t("Profil ergänzen", "Complete profile")}</Link></>}>
            <CreatorFormular ws={ws} onFertig={neu} />
          </Schritt>
          <Schritt nr={2} id="schnellstart-schritt-2" done={erledigt2} aktiv={erledigt1 && !erledigt2}
            titel={t("Eine laufende Kooperation anlegen", "Add an ongoing collaboration")}
            hinweis={erledigt1 ? t("Marke per Name, Betrag und Zeitraum. Die Marke legst du gleich hier an.", "Brand by name, amount and period. You add the brand right here.") : t("Zuerst den Creator anlegen (Schritt 1).", "First add the creator (step 1).")}
            fertig={s.deal && <>{s.deal.title} · <Link to={`/agentur/kooperationen/${s.deal.id}`}>{t("Kooperation öffnen", "Open collaboration")}</Link></>}>
            {erledigt1 && <KoopFormular ws={ws} creator={s.creator} onFertig={neu} />}
          </Schritt>
          <Schritt nr={3} id="schnellstart-schritt-3" done={erledigt3} aktiv={erledigt2 && !erledigt3}
            titel={t("Sieh dir an, was daraus wird", "See what it turns into")}
            hinweis={t("Vertrag als Entwurf aus dem Muster, Rechnung als Entwurf und der Freigabe-Link für den Content. Nur ansehen: Es wird nichts versendet.", "Contract as a draft from the template, invoice as a draft and the content approval link. Just take a look: nothing is sent.")}
            fertig={null}>
            {erledigt2 && s.deal && <Ergebnis s={s} ws={ws} onAnsehen={ansehen} onNeu={neu} />}
          </Schritt>
        </ol>
        {zahl === 3 && (
          <div className="notice row between" role="status">
            <span><strong>{t("Geschafft.", "Done.")}</strong> {t("Dein Portal ist startklar. Firmendaten und Bankverbindung fragt CreatorOffice erst ab, wenn du eine Rechnung festschreibst.", "Your portal is ready. CreatorOffice only asks for company details and bank account when you finalise an invoice.")}</span>
            <Link className="btn primary" to="/agentur">{t("Zur Übersicht", "Go to overview")}</Link>
          </div>
        )}
      </section>
      {zahl < 3 && (
        <div className="row">
          <button type="button" className="quiet" onClick={() => { merke(orgId, "spaeter"); navigate("/agentur"); }}>{t("Später, zur Übersicht", "Later, go to overview")}</button>
        </div>
      )}
      {rueckfrage}
    </main>
  );
}

function Schritt({ nr, id, done, aktiv, titel, hinweis, fertig, children }: { nr: number; id: string; done: boolean; aktiv: boolean; titel: string; hinweis: string; fertig: ReactNode; children?: ReactNode }) {
  return (
    <li id={id} className={`tk-schnellstart-schritt${done ? " done" : aktiv ? " naechster" : ""}`}>
      <span className="tk-step-mark" aria-hidden="true">{done ? "✓" : nr}</span>
      <span className="tk-step-text">
        <strong>{titel}</strong>
        {done ? <><span className="sr-only"> · {t("erledigt", "done")}</span>{fertig && <small>{fertig}</small>}</> : <small>{hinweis}</small>}
      </span>
      {(!done || nr === 3) && children && <div className="tk-schnellstart-inhalt">{children}</div>}
    </li>
  );
}

function CreatorFormular({ ws, onFertig }: { ws: StaffWorkspace; onFertig: () => Promise<unknown> }) {
  const [e, setE] = useState<CreatorEingabe>({ name: "", instagram: "" });
  const [fehler, setFehler] = useState<Partial<Record<keyof CreatorEingabe, string>>>({});
  const [busy, setBusy] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const absenden = async (ev: FormEvent) => {
    ev.preventDefault();
    const f = pruefeCreator(e);
    setFehler(f);
    if (Object.keys(f).length) return;
    setBusy(true);
    setMeldung(null);
    try {
      await legeCreatorAn(api, ws.org.org_id, e, ws.org.country);
      await onFertig();
      toast(t("Creator angelegt.", "Creator added."));
    } catch (err) {
      setMeldung(fehlerText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="stack" noValidate onSubmit={(ev) => void absenden(ev)} data-schreiben>
      <div className="tk-felder">
        <TextField label={t("Name", "Name")} value={e.name} onChange={(v) => setE({ ...e, name: v })} error={fehler.name} required autoComplete="off" placeholder={t("zum Beispiel Lena Sommer", "for example Lena Summer")} />
        <TextField label={t("Instagram-Handle (freiwillig)", "Instagram handle (optional)")} value={e.instagram} onChange={(v) => setE({ ...e, instagram: v })} error={fehler.instagram} autoComplete="off" placeholder={t("@lena.sommer", "@lena.summer")} />
      </div>
      {meldung && <p className="tk-error" role="alert">{meldung}</p>}
      <div className="row"><button className="primary" type="submit" disabled={busy} aria-busy={busy}>{busy ? t("Wird angelegt …", "Adding …") : t("Creator anlegen", "Add creator")}</button></div>
    </form>
  );
}

function KoopFormular({ ws, creator, onFertig }: { ws: StaffWorkspace; creator: SchnellstartStand["creator"]; onFertig: () => Promise<unknown> }) {
  const orgId = ws.org.org_id;
  const auswahl = useQuery({
    queryKey: ["schnellstart-auswahl", orgId],
    queryFn: async () => {
      const [talents, brands] = await Promise.all([
        api.select<{ id: string; first_name: string; last_name: string | null }>("talents", { columns: "id, first_name, last_name", filters: [["org_id", "eq", orgId], ["is_sample", "is", false], ["status", "neq", "archived"]], order: [["first_name", "asc"]] }),
        api.select<{ id: string; name: string }>("brands", { columns: "id, name", filters: [["org_id", "eq", orgId], ["is_sample", "is", false]], order: [["name", "asc"]] }),
      ]);
      return { talents, brands };
    },
  });
  const [e, setE] = useState<KoopEingabe>({ talentId: creator?.id ?? "", marke: "", markeId: null, betrag: "", von: monatJetzt(), bis: monatJetzt(), titel: "" });
  const [fehler, setFehler] = useState<Partial<Record<keyof KoopEingabe, string>>>({});
  const [busy, setBusy] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const talents = auswahl.data?.talents ?? [];
  const brands = auswahl.data?.brands ?? [];
  const setMarke = (v: string) => setE({ ...e, marke: v, markeId: brands.find((b) => b.name.trim().toLowerCase() === v.trim().toLowerCase())?.id ?? null });
  const absenden = async (ev: FormEvent) => {
    ev.preventDefault();
    const f = pruefeKoop(e);
    setFehler(f);
    if (Object.keys(f).length) return;
    setBusy(true);
    setMeldung(null);
    try {
      const tal = talents.find((x) => x.id === e.talentId);
      await legeKooperationAn(api, orgId, e, ws.org.currency || "EUR", tal ? [tal.first_name, tal.last_name].filter(Boolean).join(" ") : creator?.name ?? "");
      await onFertig();
      toast(t("Kooperation angelegt.", "Collaboration added."));
    } catch (err) {
      setMeldung(fehlerText(err));
    } finally {
      setBusy(false);
    }
  };
  const cents = centAus(e.betrag);
  return (
    <form className="stack" noValidate onSubmit={(ev) => void absenden(ev)} data-schreiben>
      <div className="tk-felder">
        <label>
          {t("Creator", "Creator")}
          <select value={e.talentId} onChange={(x) => setE({ ...e, talentId: x.target.value })} aria-invalid={fehler.talentId ? true : undefined}>
            {!e.talentId && <option value="">{t("Bitte wählen …", "Please choose …")}</option>}
            {talents.map((x) => <option key={x.id} value={x.id}>{[x.first_name, x.last_name].filter(Boolean).join(" ")}</option>)}
          </select>
          {fehler.talentId && <small className="tk-error">{fehler.talentId}</small>}
        </label>
        <TextField label={t("Marke", "Brand")} value={e.marke} onChange={setMarke} error={fehler.marke} list="schnellstart-marken" autoComplete="off"
          placeholder={t("zum Beispiel Sonnenhof Naturkosmetik", "for example Sunny Skincare")}
          hint={e.markeId ? t("Vorhandene Marke.", "Existing brand.") : e.marke.trim() ? t("Wird als neue Marke angelegt; Anschrift und Kontakt ergänzt du später.", "Will be added as a new brand; address and contact can be added later.") : undefined} />
        <datalist id="schnellstart-marken">{brands.map((b) => <option key={b.id} value={b.name} />)}</datalist>
      </div>
      <div className="tk-felder">
        <TextField label={t(`Honorar je Monat, netto (${ws.org.currency || "EUR"})`, `Fee per month, net (${ws.org.currency || "EUR"})`)} value={e.betrag} onChange={(v) => setE({ ...e, betrag: v })} error={fehler.betrag} inputMode="decimal" placeholder={t("1.500", "1,500")} />
        <TextField label={t("Von (Monat)", "From (month)")} type="month" value={e.von} onChange={(v) => setE({ ...e, von: v, bis: e.bis < v ? v : e.bis })} />
        <TextField label={t("Bis (Monat)", "To (month)")} type="month" value={e.bis} onChange={(v) => setE({ ...e, bis: v })} error={fehler.bis} min={e.von} />
      </div>
      <TextField label={t("Titel (optional)", "Title (optional)")} value={e.titel} onChange={(v) => setE({ ...e, titel: v })} maxLength={200}
        hint={t("Leer: Marke × Creator. Leistungen, Nutzungsrechte und Provision ergänzt du später in der Kooperation.", "Empty: brand × creator. Deliverables, usage rights and commission can be added later in the collaboration.")} />
      {cents !== null && e.von && e.bis && e.von !== e.bis && <small>{t(`Für ${monthLabel(e.von)} bis ${monthLabel(e.bis)} je Monat.`, `For ${monthLabel(e.von)} to ${monthLabel(e.bis)}, per month.`)}</small>}
      {meldung && <p className="tk-error" role="alert">{meldung}</p>}
      <div className="row"><button className="primary" type="submit" disabled={busy || auswahl.isLoading} aria-busy={busy}>{busy ? t("Wird angelegt …", "Adding …") : t("Kooperation anlegen", "Add collaboration")}</button></div>
    </form>
  );
}

/** Schritt 3: drei Karten, jede öffnet einen Entwurf bzw. den Freigabe-Bereich; nichts geht hinaus */
function Ergebnis({ s, ws, onAnsehen, onNeu }: { s: SchnellstartStand; ws: StaffWorkspace; onAnsehen: () => void; onNeu: () => Promise<unknown> }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const deal = s.deal!;
  const monat = (deal.month ?? `${monatJetzt()}-01`).slice(0, 7);
  const vertrag = async () => {
    setBusy(true);
    setMeldung(null);
    try {
      const id = await vertragsentwurfAusMuster(api, ws.org.org_id, { dealId: deal.id, orgName: ws.org.name, language: sprache(), today: heuteIso() });
      onAnsehen();
      await onNeu();
      navigate(id ? `/agentur/vertraege/${id}` : `/agentur/vertraege/neu?kooperation=${deal.id}`);
    } catch (e) {
      setMeldung(fehlerText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="stack">
      <div className="tk-schnellstart-karten">
        <div className="tk-schnellstart-karte">
          <strong>{t("Vertrag", "Contract")}</strong>
          <small>{t("Entwurf aus dem Muster „Kooperationsvertrag“ mit Creator, Marke und Honorar aus der Kooperation.", "Draft from the “Cooperation agreement” template with creator, brand and fee from the collaboration.")}</small>
          {s.dealContract
            ? <Link className="btn" to={`/agentur/vertraege/${s.dealContract}`} onClick={onAnsehen}>{t("Vertragsentwurf öffnen", "Open contract draft")}</Link>
            : <button type="button" className="btn" disabled={busy} aria-busy={busy} onClick={() => void vertrag()}>{busy ? t("Wird erstellt …", "Creating …") : t("Vertragsentwurf erstellen", "Create contract draft")}</button>}
        </div>
        <div className="tk-schnellstart-karte">
          <strong>{t("Rechnung", "Invoice")}</strong>
          <small>{t("Entwurf an die Marke, vorbelegt aus der Kooperation. Festschreiben erst, wenn du so weit bist.", "Draft to the brand, prefilled from the collaboration. Finalise only when you are ready.")}</small>
          <Link className="btn" to={s.dealInvoice ? `/agentur/rechnungen/${s.dealInvoice}` : `/agentur/rechnungen/neu?kooperation=${deal.id}&monat=${monat}`} onClick={onAnsehen}>
            {s.dealInvoice ? t("Rechnungsentwurf öffnen", "Open invoice draft") : t("Rechnungsentwurf ansehen", "View invoice draft")}
          </Link>
        </div>
        <div className="tk-schnellstart-karte">
          <strong>{t("Content-Freigabe", "Content approval")}</strong>
          <small>{t("Reicht der Creator Content ein, schickst du der Marke einen Link zur Freigabe, ohne Konto für die Marke.", "When the creator submits content, you send the brand an approval link, no account needed for the brand.")}</small>
          <Link className="btn" to={`/agentur/kooperationen/${deal.id}/content`} onClick={onAnsehen}>{t("Freigabe ansehen", "View approval")}</Link>
        </div>
      </div>
      {meldung && <p className="tk-error" role="alert">{meldung}</p>}
    </div>
  );
}

/** Beispieldaten geladen: Wegweiser zu allen Beispiel-Stücken und Löschen */
function BeispielKarte({ s, orgId, orgName, onLoeschen, onNeu }: { s: SchnellstartStand; orgId: string; orgName: string; onLoeschen: () => void; onNeu: () => Promise<unknown> }) {
  const [busy, setBusy] = useState(false);
  const vertrag = async () => {
    if (!s.deal_id) return;
    setBusy(true);
    try {
      await vertragsentwurfAusMuster(api, orgId, { dealId: s.deal_id, orgName, language: sprache(), today: heuteIso() });
      await onNeu();
    } catch (e) {
      toast(fehlerText(e), "alert");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="panel stack tk-beispiel-karte" aria-labelledby="schnellstart-beispiel">
      <div className="row between">
        <h2 id="schnellstart-beispiel">{t("Beispieldaten", "Sample data")} <span className="pill info">{t("Beispiel", "Sample")}</span></h2>
        <button type="button" className="quiet danger" onClick={onLoeschen}>{t("Beispieldaten löschen", "Delete sample data")}</button>
      </div>
      <p>{t("So sieht eine Kooperation in CreatorOffice aus. Alles ist erfunden, sendet nichts (auch keine Mails an @example.com) und zählt nicht in deinen Tarif.", "This is what a collaboration looks like in CreatorOffice. Everything is made up, sends nothing (not even emails to @example.com) and does not count towards your plan.")}</p>
      <div className="tk-schnellstart-karten">
        {s.talent_id && <Link className="tk-schnellstart-karte" to={`/agentur/creator/${s.talent_id}`}><strong>{t("Creator", "Creator")}</strong><small>Mila Beispiel</small></Link>}
        {s.deal_id && <Link className="tk-schnellstart-karte" to={`/agentur/kooperationen/${s.deal_id}`}><strong>{t("Kooperation", "Collaboration")}</strong><small>{t("mit der Beispielmarke GmbH", "with Beispielmarke GmbH")}</small></Link>}
        {s.contract_id
          ? <Link className="tk-schnellstart-karte" to={`/agentur/vertraege/${s.contract_id}`}><strong>{t("Vertrag (Entwurf)", "Contract (draft)")}</strong><small>{t("aus dem Muster", "from the template")}</small></Link>
          : s.deal_id && <button type="button" className="tk-schnellstart-karte" disabled={busy} onClick={() => void vertrag()}><strong>{t("Vertrag (Entwurf)", "Contract (draft)")}</strong><small>{busy ? t("Wird erstellt …", "Creating …") : t("aus dem Muster erstellen", "create from the template")}</small></button>}
        {s.review_token && <a className="tk-schnellstart-karte" href={`/freigabe/${s.review_token}`} target="_blank" rel="noopener"><strong>{t("Content-Freigabe", "Content approval")}</strong><small>{t("Link, wie ihn die Marke sieht", "Link as the brand sees it")}</small></a>}
        {s.invoice_id && <Link className="tk-schnellstart-karte" to={`/agentur/rechnungen/${s.invoice_id}`}><strong>{t("Rechnung (Entwurf)", "Invoice (draft)")}</strong><small>{t("an die Beispielmarke", "to the sample brand")}</small></Link>}
      </div>
    </section>
  );
}

/**
 * Hinweis im ganzen Agentur-Portal, solange Beispieldaten da sind (nur Admins): klar als Beispiel erkennbar und mit einem Klick zu
 * löschen. Ist der erste echte Creator angelegt, fragt er einmal, ob die Beispieldaten jetzt weg sollen.
 */
export function BeispielHinweis({ ws }: { ws: StaffWorkspace }) {
  const orgId = ws.org.org_id;
  const { pathname } = useLocation();
  const qc = useQueryClient();
  const [behalten, setBehalten] = useState(() => gemerkt(orgId, "beispiel-frage"));
  const [busy, setBusy] = useState(false);
  const rundgang = useRundgang();
  const stand = useQuery({
    queryKey: [...schnellstartKey(orgId), "hinweis"], enabled: ws.isChef && !ws.isCreator, staleTime: 30_000,
    queryFn: () => api.read<{ has_sample: boolean; creators: number } | string>("sample_data_status", { p_org: orgId }).then((r) => (typeof r === "string" ? JSON.parse(r) as { has_sample: boolean; creators: number } : r)),
  });
  // K-573: während des Rundgangs mit Coco eingeklappt; nach Beispieldaten aus Schritt 1 fragt der Abschluss „behalten oder entfernen?“
  if (!stand.data?.has_sample || pathname.startsWith("/agentur/schnellstart") || rundgang) return null;
  const loeschen = async () => {
    setBusy(true);
    try {
      await loescheBeispieldaten(api, orgId);
      await qc.invalidateQueries();
      toast(t("Beispieldaten gelöscht.", "Sample data deleted."));
    } catch (e) {
      toast(fehlerText(e), "alert");
    } finally {
      setBusy(false);
    }
  };
  const frage = stand.data.creators > 0 && !behalten;
  return (
    <div className="content tk-rahmen-hinweise tk-noprint">
      <div className="tk-start-banner tk-beispiel-banner" role={frage ? "alert" : "note"}>
        <span className="pill info">{t("Beispiel", "Sample")}</span>
        <span className="tk-start-banner-text">
          {frage
            ? <><strong>{t("Dein erster eigener Creator ist angelegt.", "Your first own creator has been added.")}</strong><small>{t("Sollen die Beispieldaten jetzt weg? Deine eigenen Daten bleiben.", "Remove the sample data now? Your own data stays.")}</small></>
            : <><strong>{t("Du siehst Beispieldaten.", "You are looking at sample data.")}</strong><small>{t("Sie senden nichts und zählen nicht in deinen Tarif.", "It sends nothing and does not count towards your plan.")}</small></>}
        </span>
        {frage
          ? <>
            <button type="button" className="btn primary" disabled={busy} aria-busy={busy} onClick={() => void loeschen()}>{t("Ja, Beispieldaten löschen", "Yes, delete sample data")}</button>
            <button type="button" className="btn" onClick={() => { merke(orgId, "beispiel-frage"); setBehalten(true); }}>{t("Behalten", "Keep")}</button>
          </>
          : <>
            <Link className="btn quiet" to="/agentur/schnellstart">{t("Schnellstart", "Quick start")}</Link>
            <button type="button" className="btn" disabled={busy} aria-busy={busy} onClick={() => void loeschen()}>{t("Beispieldaten löschen", "Delete sample data")}</button>
          </>}
      </div>
    </div>
  );
}
